/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useContext } from 'react';
import TextMarkup from './textMarkup';
import { v4 as uuidv4 } from 'uuid';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { ColorAliases } from 'styles/variables';

export interface HighlightWorkspaceTermsProps extends HighlightTermsProps {
	/** maps to children for compatibility */
	text?: string;
}
export const HighlightWorkspaceTerms: React.FC<HighlightWorkspaceTermsProps> = ({
	text,
	children,
	noTextMessage,
	wrapperElement,
	className,
}) => {
	const {
		state: {
			explorer: {
				filters: {
					search: { terms } = {
						terms: [],
					},
				},
			},
		},
	} = useContext(WorkspaceContext);

	return (
		<HighlightTerms
			{...{
				children: text ?? children,
				noTextMessage,
				wrapperElement,
				terms,
				className,
			}}
		/>
	);
};

export interface HighlightTermsProps {
	terms?: string[];
	wrapperElement?: keyof JSX.IntrinsicElements;
	noTextMessage?: string;
	className?: string;
	title?: string;
	ellipsize?: boolean;
}
export const HighlightTerms: React.FC<HighlightTermsProps> = ({
	children,
	terms = [],
	wrapperElement: Element = 'span',
	noTextMessage = 'No text',
	className,
	...rest
}) => {
	if (!children) {
		return (
			<Element {...rest} className={className}>
				<em
					children={noTextMessage}
					css={css`
						color: ${ColorAliases.PT_TEXT_COLOR_DISABLED};
					`}
					className={className}
				/>
			</Element>
		);
	}

	if (terms.length === 0) {
		return (
			<Element
				{...rest}
				children={children}
				className={className}
			/>
		);
	}

	const text = children.toString();
	const highlights = searchWithOffsets(text, terms);
	return <TextMarkup {...rest} highlights={highlights} text={text} componentType={Element} className={className} />;
};

/** Find matches for `terms` in description*/
function searchWithOffsets(description: string, terms: string[]) {
	// sanitize terms
	const formattedTerms = terms.map((term) => term.replace(/[#-.]|[[-^]|[?|{}]/g, '\\$&'));

	const token = new RegExp(`(${formattedTerms.join('|')})`, 'gi');
	// @Ryan: removed wordbreak \b wrappers to match sub-word strings. Matches how search works on the server?
	// terms = ['magnetic'] matches in 'Electromagnetic' 👍

	// @James: That's right! Before, in the filter network request we we're using the SRS $token search (which matches
	// words). Now we're using $c (contains) which matches all sub-strings.

	// regex match all search terms in the description
	const regexIterator = description.matchAll(token);

	// Format results
	return Array.from(regexIterator).map((match) => ({
		id: uuidv4(),
		value: match[0],
		start: match.index,
		end: match[0].length + match.index,
	}));
}
